import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ConsentFormHeader from 'components/molecules/consent-form/header';
import ConsentPatientInfo from 'components/molecules/consent-form/patient-info';
import ConsentSectionTitle from 'components/molecules/consent-form/section-title';
import ConsentFormFooter from 'components/molecules/consent-form/footer';
import ConsentSignatureBlock from 'components/molecules/consent-form/signature-block';
import ConsentFormCheckbox from 'components/molecules/consent-form/checkbox';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { isValidGuid } from 'infrastructure/functions/is-valid-guid';
import { showResult } from 'infrastructure/hooks/utils';
import { Trans, useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import { usePatientInsurance } from 'infrastructure/hooks/patient/use-patient-insurance';
import { DateFormatter } from 'infrastructure/functions';
import { Routes } from 'router/routes';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { useApcmConsentForm } from 'infrastructure/hooks/patient/use-apcm-consent-ccm-form';
import { useGetAdminPanelPatients } from 'infrastructure/hooks/patient/use-admin-panel-patients';

import s from './styles.module.scss';

import type { Patient } from 'infrastructure/classes/patient/patient';
import type { TPatientInsurances } from 'infrastructure/interfaces';

interface IProps {
  patientInfo?: Patient;
  insurances: TPatientInsurances;
  onConsent: (val: boolean) => void;
  consentInfo: string | null;
  patientConsentInfo: string | null;
}

const ApcmTemplate: React.FC<IProps> = ({
  patientInfo,
  insurances,
  onConsent,
  consentInfo,
  patientConsentInfo,
}) => {
  const { t } = useTranslation();

  const fields = useMemo(() => {
    const insuranceNumber =
      insurances.length > 0 ? insurances[0].number : t('labels.noInsurance');

    return [
      {
        name: t('labels.patientName'),
        value: `${patientInfo?.loginInfo?.firstName} ${patientInfo?.loginInfo?.lastName}`,
      },
      {
        name: t('labels.dob'),
        value: DateFormatter({
          date: patientInfo?.loginInfo?.birthDate,
        }),
      },
      {
        name: t('labels.primaryInsurance'),
        value: insuranceNumber,
      },
      {
        name: t('labels.memberId'),
        value: `${patientInfo?.guid}`,
      },
      {
        name: t('labels.phoneNumber'),
        value: `${patientInfo?.loginInfo?.phone}`,
      },
      {
        name: t('labels.preferredEmail'),
        value: `${patientInfo?.loginInfo?.email}`,
      },
    ];
  }, [t, patientInfo, insurances]);

  const informedConsentList = t('apcmConsentForm.informedConsentList', {
    returnObjects: true,
  });
  const byParticipatingList = t('apcmConsentForm.byParticipatingList', {
    returnObjects: true,
  });
  const yourRightsList = t('apcmConsentForm.yourRightsList', {
    returnObjects: true,
  });

  return (
    <div className={s.page}>
      <ConsentFormHeader
        title={t('apcmConsentForm.title')}
        subTitle={t('apcmConsentForm.subTitle')}
      />
      <ConsentPatientInfo fields={fields} />
      <ConsentSectionTitle>
        {t('apcmConsentForm.informedConsentTitle')}
      </ConsentSectionTitle>
      <ul>
        {informedConsentList.map((item, i) => (
          <li key={i}>
            <Trans key={i} i18nKey={item as any} components={{ b: <b /> }} />
          </li>
        ))}
      </ul>
      <ConsentSectionTitle>
        {t('apcmConsentForm.byParticipatingTitle')}
      </ConsentSectionTitle>
      <ul>
        {byParticipatingList.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>

      <ConsentSectionTitle>
        {t('apcmConsentForm.yourRightsTitle')}
      </ConsentSectionTitle>
      <ul>
        {yourRightsList.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      <ConsentFormCheckbox
        value={patientInfo?.apcmConsentFormStatus}
        label={t('apcmConsentForm.checkboxLabel')}
        onCheck={onConsent}
      />
      <p>
        {consentInfo}
        <br />
        {patientConsentInfo}
      </p>
      <ConsentSignatureBlock
        name={t('apcmConsentForm.signaturePatient')}
        underline={false}
      />
      <ConsentSignatureBlock
        name={t('apcmConsentForm.signatureAuthorized')}
        showReason
      />
      <ConsentSignatureBlock name={t('apcmConsentForm.signatureAgency')} />
      <ConsentFormFooter />
    </div>
  );
};

const ApcmConsentFormPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { patientGuid } = useParams();

  useEffect(() => {
    if (!patientGuid || !isValidGuid(patientGuid)) {
      showResult(t('errors.invalidGuid'));
      navigate(Routes.notFound, { replace: true });
    }
  }, [patientGuid, navigate]);

  const [insurances, setInsurances] = useState<TPatientInsurances>([]);

  const { loading: insuranceLoading, loadPatientInsurance } =
    usePatientInsurance({
      patientGuid: patientGuid!,
    });
  const {
    loading: patientInfoLoading,
    patientInfo,
    invalidateCache,
  } = usePatientInfo({
    patientGuid: patientGuid!,
  });
  const { loading: consentLoading, consentForm } = useApcmConsentForm();
  const { patients, isFetching } = useGetAdminPanelPatients({
    patientGuid,
  });

  const apcmConsentFormData = patients?.items?.[0]?.apcmConsentFormData;
  const loading = insuranceLoading || patientInfoLoading || isFetching;
  const formSignedAt = apcmConsentFormData?.signedAt;
  const formObtainedBy = apcmConsentFormData?.obtainedBy ?? '';
  const consentInfo = formSignedAt
    ? `${t('labels.consentObtainedBy')}: ${formObtainedBy}, ${t('labels.on')}
  ${formSignedAt}`
    : null;
  const patientConsentInfo = formSignedAt
    ? `${t('labels.patientVerbalConsent')}: ${formObtainedBy}
    , ${t('labels.on')}
    ${formSignedAt}`
    : null;

  const handleConsent = useCallback(
    async (isAgree: boolean) => {
      await consentForm(patientGuid!, isAgree).then(() => {
        invalidateCache();
        navigate(Routes.adminPatient(patientGuid!), { replace: true });
      });
    },
    [patientGuid, consentForm],
  );

  useEffect(() => {
    if (patientGuid) {
      loadPatientInsurance().then((res) => {
        if (res) setInsurances(res);
      });
    }
  }, [patientGuid]);

  return (
    <BaseLoader loading={loading}>
      <BaseSpinWrapper spinning={consentLoading}>
        <ApcmTemplate
          patientInfo={patientInfo}
          insurances={insurances}
          onConsent={handleConsent}
          consentInfo={consentInfo}
          patientConsentInfo={patientConsentInfo}
        />
      </BaseSpinWrapper>
    </BaseLoader>
  );
};

export default ApcmConsentFormPage;
